import { useReducer } from 'react';
import { FormField, TextInput } from 'grommet';
import { Disclaimer, HelpWith } from '@components/basicComponents';
import { HelpWithType } from '@customTypes/enums';
import type { FormFieldsProps } from '@customTypes/types';
import type { OSQBMSupportFormFieldsState } from '@customTypes/stateTypes';

import {
  helpWithOptions,
  arubaCentralOptions,
  computeOpsManagementOptions,
  computeOpsManagementOrdersOptions,
  dataServicesOptions,
  hpeGreenLakeFlexOptions,
  hpeGreenLakePlatformOptions,
  // meteringOptions,
} from './OSQBMSupportFormOptions';

export const OSQBMSupportFormFields = (props: FormFieldsProps): JSX.Element => {
  const [state, updateState] = useReducer(
    (prev: OSQBMSupportFormFieldsState, newState: Partial<OSQBMSupportFormFieldsState>) => ({
      ...prev,
      ...newState,
    }),
    {
      helpWith: '',
      helpWithSecondary: '',
      secondDropdownOptions: null,
      showNotification: false,
      showComputeOpsManagementOrders: false,
      showOrderNumber: false,
      showSubscriptionID: false,
      showAccountName: false,
      showOpportunityID: false,
      showInvoiceID: false,
      showMetering: false,
    }
  );

  const helpWithChange = (option: string): void => {
    if (option === state.helpWith) {
      return;
    }

    clearSecondSelectState();

    //TODO remove when 2 or more option will be added to hpeGreenLakePlatform
    if (option === helpWithOptions.hpeGreenLakePlatform) {
      updateState({
        helpWith: option,
        secondDropdownOptions: hpeGreenLakePlatformOptions,
        helpWithSecondary: Object.values(hpeGreenLakePlatformOptions)[0],
       });
       props.onDetailsChange({ showDetails: true, isRequiredDetails: true, numFiles: 0 });
      return;
    }

    switch (option) {
      case helpWithOptions.arubaCentral:
        updateState({ secondDropdownOptions: arubaCentralOptions });
        break;
      case helpWithOptions.computeOpsManagement:
        updateState({ secondDropdownOptions: computeOpsManagementOptions });
        break;
      case helpWithOptions.dataServices:
        updateState({ secondDropdownOptions: dataServicesOptions });
        break;
      case helpWithOptions.hpeGreenLakeFlex:
        updateState({ secondDropdownOptions: hpeGreenLakeFlexOptions });
        break;
      //TODO uncomment when 2 or more option will be added to hpeGreenLakePlatform
      // case helpWithOptions.hpeGreenLakePlatform:
      //   updateState({ secondDropdownOptions: hpeGreenLakePlatformOptions });
      //   break;
    }

    updateState({ helpWith: option, helpWithSecondary: '' });
    props.onDetailsChange({ showDetails: false, numFiles: 0 });
  };

  const helpWithSecondaryChange = (option: string): void => {
    if (option === state.helpWithSecondary) {
      return;
    }

    clearSecondSelectState();

    updateState({ helpWithSecondary: option });

    switch (option) {
      case 'Orders':
        if (state.helpWith === helpWithOptions.computeOpsManagement) {
          updateState({ showComputeOpsManagementOrders: true });
          props.onDetailsChange({ numFiles: 0 });
        } else {
          updateState({ showOrderNumber: true });
          props.onDetailsChange({ showDetails: true, isRequiredDetails: true });
        }
        break;
      case 'Subscription':
        updateState({ showSubscriptionID: true });
        props.onDetailsChange({ showDetails: true, isRequiredDetails: true });
        break;
      case 'Subscriptions':
        updateState({ showSubscriptionID: true, showOrderNumber: true });
        props.onDetailsChange({ showDetails: true, isRequiredDetails: true });
        break;
      case 'Quotes':
        updateState({ showOpportunityID: true });
        props.onDetailsChange({ showDetails: true, isRequiredDetails: true });
        break;
      case 'Billing':
        updateState({ showInvoiceID: true });
        props.onDetailsChange({ showDetails: true, isRequiredDetails: true });
        break;
      case 'Metering':
        // updateState({ showMetering: true });
        (state.helpWith === helpWithOptions.hpeGreenLakeFlex) && updateState({ showNotification: true });
        props.onDetailsChange({ showDetails: true, isRequiredDetails: true });
        break;
      case hpeGreenLakeFlexOptions.monthlyCharges:
      case hpeGreenLakeFlexOptions.consumptionAnalytics:
      case hpeGreenLakeFlexOptions.capacityPlanning:
        updateState({ showNotification: true, showAccountName: true });
        props.onDetailsChange({ showDetails: true, isRequiredDetails: true });
        break;
      case hpeGreenLakeFlexOptions.partnerBillingAccountsManager:
      case hpeGreenLakePlatformOptions.partnerBillingAccountsManager:
        updateState({ showNotification: true });
        props.onDetailsChange({ showDetails: true, isRequiredDetails: true });
        break;
      default:
        props.onDetailsChange({ showDetails: true, isRequiredDetails: true });
    }
  };

  const helpWithThirdChange = (option: string): void => {
    clearThirdSelectState();

    switch (option) {
      case computeOpsManagementOrdersOptions.submittedOrder:
        updateState({ showOrderNumber: true });
        props.onDetailsChange({ showDetails: true, isRequiredDetails: true });
        break;
      case computeOpsManagementOrdersOptions.newSubscriptionOrder:
        props.onDetailsChange({ showDetails: true, isRequiredDetails: true });
        break;
      default:
        props.onDetailsChange({ showDetails: true, isRequiredDetails: true });
    }
  };

  const clearState = (): void => {
    clearSecondSelectState();
    updateState({ helpWith: '', helpWithSecondary: '', secondDropdownOptions: null });
  };

  props.onReset && props.onReset(clearState);

  const clearSecondSelectState = (): void => {
    clearThirdSelectState();

    updateState({
      helpWithSecondary: '',
      showNotification: false,
      showComputeOpsManagementOrders: false,
      showSubscriptionID: false,
      showOrderNumber: false,
      showOpportunityID: false,
      showInvoiceID: false,
      showMetering: false,
      showAccountName: false,
    });

    props.onDetailsChange({ showDetails: false });
  };

  const clearThirdSelectState = (): void => {
    updateState({ showOrderNumber: false });
    props.onDetailsChange({ showDetails: false });
  };

  return (
    <>
      <HelpWith
        selectType={!props.isConsolidated ? HelpWithType.Application : HelpWithType.Second}
        options={Object.values(helpWithOptions)}
        onChange={helpWithChange}
      />

      {state.secondDropdownOptions &&
        <HelpWith
          selectType={!props.isConsolidated ? HelpWithType.Second : HelpWithType.Third}
          options={Object.values(state.secondDropdownOptions)}
          value={state.helpWithSecondary}
          onChange={helpWithSecondaryChange}
        />
      }

      {state.showNotification &&
        <Disclaimer
          text={'Please contact your HPE Account Support Manager or HPE representative before logging a request or ask them to submit a support issue on your behalf.'}
        />
      }

      {state.showComputeOpsManagementOrders &&
        <HelpWith
          selectType={!props.isConsolidated ? HelpWithType.Third : HelpWithType.Fourth}
          options={Object.values(computeOpsManagementOrdersOptions)}
          onChange={helpWithThirdChange}
        />
      }

      {state.showSubscriptionID &&
        <FormField name="subscriptionId" htmlFor="subscription-id" label="Subscription ID">
          <TextInput
            id="subscription-id"
            name="subscriptionId"
            placeholder="Subscription ID (ex: EB0839D9C004445829) 18 characters length"
            maxLength={18}
          />
        </FormField>
      }

      {state.showOrderNumber &&
        <FormField name="orderNumber" htmlFor="order-number" label="Order number" required>
          <TextInput id="order-number" name="orderNumber" placeholder="Order number" />
        </FormField>
      }

      {state.showOpportunityID &&
        <FormField name="opportunityId" htmlFor="opportunity" label="Opportunity ID">
          <TextInput id="opportunity" name="opportunityId" placeholder="Opportunity ID" />
        </FormField>
      }

      {state.showInvoiceID &&
        <FormField name="invoiceId" htmlFor="invoice" label="Invoice ID">
          <TextInput id="invoice" name="invoiceId" placeholder="Invoice ID" />
        </FormField>
      }

      {state.showAccountName &&
        <FormField name="accountName" htmlFor="account-name" label="Account name">
          <TextInput id="account-name" name="accountName" placeholder="Account name" />
        </FormField>
      }

      {/* TODO: change to helpWithFourth if isConsolidated */}
      {/* {state.showMetering &&
        <FormField name="helpWithThird" htmlFor="metering" label="Services" required>
          <Select id="metering" name="helpWithThird" placeholder="Please Select a Service" options={meteringOptions} />
        </FormField>
      } */}
    </>
  );
};
